import { MutableRefObject, ReactNode, CSSProperties, RefObject, } from "react";
import { IBtnList } from "../ButtonGroup/buttonGroup";
import { DashboardComponentAction } from "../constants/interface";

interface DragDropAction {
  /** 获取拖拽项数据 */
  getDragItems: () => DragDropOption[];
  /** 新增拖拽选项 */
  addDragItem: (dragItem: DragDropOption) => void;
  /** 删除拖拽选项 */
  deleteDragItem: (dragItemName: string) => void;
  /** 获取拖拽项ref */
  getDragItemRef: (name: string) => DashboardComponentAction;
  /** 全屏播放 */
  fullScreen: (name?: string) => void;
};

export type DragDropActionType = DragDropAction | undefined;
export interface DragDropProps {
  /** 对外方法 */
  actionRef?: MutableRefObject<DragDropActionType>;
  /** 网格数 */
  grid?: number;
  /** 单个网格高度 */
  height?: number;
  /** 全局设置是否可以拖动 */
  isMove?: boolean;
  /** 全局设置是否可以改变大小 */
  isChangeSize?: boolean;
  /** 网格配置 null则不显示 */
  gridConfig?: DragGridProps | null;
  /** 拖拽项 */
  options?: DragDropOption[];
  /** 是否正在加载中 */
  loading?: boolean;
  /**
   * 点击操作按钮
   * @param key 按钮key
   * @param widgetInfo 部件信息
   * @returns 
   */
  onClickOperation?: (key: string, widgetInfo: DragDropOption) => void;
  /**
   * 点击其他操作按钮
   * @param key 按钮key
   * @param widgetInfo 部件信息
   * @returns 
   */
  onClickOtherOperation?: (key: string, widgetInfo: DragDropOption) => void;
};

/** 网格配置 */
export interface DragGridProps {
  /** 网格边距 */
  padding?: number;
  /** 网格边框颜色 */
  color?: string;
};
/** 拖动组 */
export interface OptionsProps {}
/** 拖动项 */
export interface OptionItemProps extends DragDropOption {
  /** 预览组件 */
  previewRef: MutableRefObject<DragpreviewActionType>;
}
/** 预览项 */
export interface DragpreviewProps {
  actionRef: MutableRefObject<DragpreviewActionType>;
};
/** size改变 */
export interface DragSizeProps extends DragItemSize {
  /** itemRef */
  itemRef: RefObject<HTMLDivElement>;
  /** 最小列 */
  minColumn?: number;
  /** 最小行 */
  minRow?: number;
  /** 单个网格高度 */
  height: number;
  /** 单个网格宽度 */
  width: number;
  /** 网格数 */
  grid: number;
  /** 除当前拖拽项以外，其他拖拽项网格占用情况 */
  otherGridUseData: IGridUseData[];
  /** 改变大小开始回调 */
  onChangeStart: () => void;
  /** 改变大小过程中回调 */
  onChangeMove: (data: SizeChangeData) => void;
  /** 改变大小结束回调 */
  onChangeEnd: (data: SizeChangeData) => void;
};
/** size改变类型 */
export type SizeType = 'top' | 'right' | 'bottom' | 'left';
/** 拖拽项 */
export interface DragDropOption {
  /** 名称 唯一 */
  name: string;
  /** 编码 */
  code?: string;
  /** 是否可以拖动 */
  isMove?: boolean;
  /** 是否可以改变大小 */
  isChangeSize?: boolean;
  /** left网格定位 */
  left?: number;
  /** top网格定位 */
  top?: number;
  /** 列 */
  column?: number;
  /** 行 */
  row?: number;
  /** 最小列 */
  minColumn?: number;
  /** 最小行 */
  minRow?: number;
  /** 内容 */
  container?: ReactNode;
  /** 左上角拖动状态下显示的图标 */
  moveIcon?: ReactNode;
  /** 右上角更多操作按钮显示的图标 */
  moreIcon?: ReactNode;
  /** 标题 */
  title?: ReactNode;
  /** 是否可以全屏查看 默认开启 */
  fullScreen?: boolean;
  /** 是否可以刷新 默认开启 */
  refresh?: boolean;
  /** 是否可以复制 默认开启 */
  isCopy?: boolean;
  /** 是否可以删除 默认开启 */
  isDelete?: boolean;
  /** 是否可以编辑 默认开启 */
  isEdit?: boolean;
  /** 是否通过接口复制过来的部件 */
  isApiCopy?: boolean;
  /** 副标题 */
  subTitle?: ReactNode;
  /** 标题前部 */
  beforeNode?: ReactNode;
  /** 标题后部 */
  afterNode?: ReactNode;
  /** 操作按钮 */
  otherOperations?: IBtnList[];
  /** 描述 */
  description?: ReactNode;
  /** 其他 */
  [key: string]: any;
}
/** 全屏组件 */
export interface DropfullScreenProps {
  actionRef: MutableRefObject<DropfullScreenActionType>;
}

/** 容器尺寸 */
export interface WrapSize {
  /** 宽度 */
  width: number;
  /** 高度 */
  height: number;
}
/** 拖动数据 */
export interface MoveData {
  /** x */
  x: number;
  /** y */
  y: number;
}
/** 拖动数据 */
export interface SizeChangeData {
  /** left网格偏移数 */
  left: number;
  /** top网格偏移数 */
  top: number;
  /** 列偏移数 */
  column: number;
  /** 行偏移数 */
  row: number;
}
export interface DragItemSize {
  /** 列 */
  column: number;
  /** 行 */
  row: number;
  /** left网格定位 */
  left: number;
  /** top网格定位 */
  top: number;
}
/** 改变容器多边数据 */
export interface DragChangeSizes {
  /** X轴偏移量 */
  moveX: number;
  /** Y轴偏移量 */
  moveY: number;
  /** left网格定位 */
  left: number;
  /** top网格定位 */
  top: number;
  /** 单个网格宽度 */
  width: number;
  /** 单个网格高度 */
  height: number;
  /** item宽度占用网格数 */
  column: number;
  /** item高度占用网格数 */
  row: number;
  /** 网格等分数 */
  grid: number;
  /** item宽度最小占用网格数 */
  minColumn?: number;
  /** item高度最小占用网格数 */
  minRow?: number;
}
/** 预览组件对外方法 */
interface DragpreviewAction {
  /** 显示 */
  show: (style: CSSProperties) => void;
  /** 隐藏 */
  hide: () => void;
  /** 移动 */
  move: (style: CSSProperties) => void;
}
export type DragpreviewActionType = DragpreviewAction | undefined;
/** 全屏组件对外方法 */
interface DropfullScreenAction {
  /**
   * 全屏显示
   * @param name 当前全屏项名称
   * @returns 
   */
  show: (name: string) => void;
};
export type DropfullScreenActionType = DropfullScreenAction | undefined;
/** 重合情况 */
export type coincidenceSituation = 'none' | 'part' | 'all';
/** 网格占用情况 */
export interface IGridUseData {
  /** 占用行 */
  row: number;
  /** 当前行列被占用的单元格 */
  columns: number[];
};
/** 全屏key */
export const FULL_SCREEN_KEY = 'FULL_SCREEN_KEY';
/** 刷新key */
export const REFRESH_KEY = 'REFRESH_KEY';
/** 复制key */
export const COPY_KEY = 'COPY_KEY';
/** 删除key */
export const DELETE_KEY = 'DELETE_KEY';
/** 编辑key */
export const EDIT_KEY = 'EDIT_KEY';
/** 是否为初始化渲染传入数据 */
export interface IFirstDataProps {
  /** 拖拽项 */
  options: boolean;
  /** 拖动 */
  isMove: boolean;
}
